import React, { useState, useEffect, useMemo, Dispatch, SetStateAction } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import MultiSelectDropdownNoCreate from '../../dropdowns/MultiSelectDropdownNoCreate';
import type {
  SelectedTicket,
  EventData,
  TicketAttendee,
  Ticket,
  TicketInfo,
  EventFormData,
  Transaction,
} from '../../drawers/types/EventPaymentTypes';
import type { Merchant } from '../../../types/common';
import {
  buildDashboardInventoryCredits,
  calculateDashboardTicketRowSubtotal,
} from '../../../utils/eventTicketPurchase';
import {
  formatOfflinePaymentMethod,
  getEventOfflinePaymentMethods,
} from '../../../utils/offlinePaymentMethods';

const generatePurchasedGroupID = (): string => {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
  throw new Error('This browser cannot create a secure ticket group identifier');
};

const generateConfirmationID = (): string =>
  Array.from(
    { length: 7 },
    () =>
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'[
        Math.floor(Math.random() * 62)
      ],
  ).join('');

// Props interface for the EventInformation component
interface EventInformationProps {
  formData: EventFormData;
  setFormData: Dispatch<SetStateAction<EventFormData>>;
  handleFormDataChange: (updates: Partial<EventFormData>) => void;
  merchant: Merchant | null;
  invalidFields: Set<string>;
  handleFieldFocus: (fieldName: string) => void;
  isEditMode: boolean;
  paymentType: string;
  isDrawerOpen: boolean;
  transaction?: Transaction;
  validatePromoCode: (code: string) => Promise<void>;
  removePromoCode: (code: string) => void;
  isPromoProcessing: boolean;
  promoMessage: string;
}

export default function EventInformation({
  formData,
  setFormData,
  handleFormDataChange,
  merchant,
  invalidFields,
  handleFieldFocus,
  isEditMode,
  paymentType,
  isDrawerOpen,
  transaction,
  validatePromoCode,
  removePromoCode,
  isPromoProcessing,
  promoMessage,
}: EventInformationProps): React.ReactElement {
  const [possibleForms, setPossibleForms] = useState<EventData[]>([]);
  const [selectedTickets, setSelectedTickets] = useState<SelectedTicket[]>([]);
  const inventoryCredits = useMemo(
    () => buildDashboardInventoryCredits(transaction),
    [transaction],
  );

  // Populate possibleForms when drawer opens and API data is available
  useEffect(() => {
    if (!isDrawerOpen) return;

    if (formData?.formToFormDataMap && Object.keys(formData.formToFormDataMap).length > 0) {
      const formsArray = Object.values(formData.formToFormDataMap) as EventData[];
      if (formsArray.length > 0) {
        setPossibleForms(formsArray);
      }
    }
  }, [formData?.formToFormDataMap, isDrawerOpen]);

  // Initialize selectedTickets from formData.tickets in edit mode
  useEffect(() => {
    if (isEditMode && formData.tickets && formData.tickets.length > 0) {
      const hasRealTicketData = formData.tickets.some(
        (ticket) => ticket.ticketType && ticket.ticketID,
      );

      if (hasRealTicketData && selectedTickets.length === 0) {
        const convertedSelectedTickets: SelectedTicket[] = formData.tickets.map(
          (ticket, index) => ({
            ticketIndex: index,
            ticketType: ticket.ticketType || '',
            ticketID: ticket.ticketID || '',
            ticketPrice: ticket.ticketPrice || 0,
            seats: ticket.seats || 1,
            customInputs: [],
          }),
        );
        setSelectedTickets(convertedSelectedTickets);
      }
    }
  }, [isEditMode, formData.tickets, selectedTickets.length]);

  // Set selectedEventData when possibleForms becomes available in edit mode
  useEffect(() => {
    if (
      isEditMode &&
      possibleForms.length > 0 &&
      (formData.selectedEvent || formData.formName) &&
      !formData.selectedEventData
    ) {
      const eventName = formData.selectedEvent || formData.formName;
      const selectedEvent = possibleForms.find(
        (f) => f.formName === eventName && f.formType === 'EVENT',
      );
      if (selectedEvent) {
        setFormData((prev) => ({
          ...prev,
          selectedEvent: eventName,
          selectedEventData: selectedEvent as unknown as EventData,
        }));
      }
    }
  }, [
    isEditMode,
    possibleForms,
    formData.selectedEvent,
    formData.formName,
    formData.selectedEventData,
    setFormData,
  ]);

  // Transaction attendee rows predate the event-definition fields needed by the
  // minimum-seat editor. Hydrate those fields once the current event is loaded.
  useEffect(() => {
    if (!isEditMode || !formData.selectedEventData) return;
    setFormData((prev) => {
      let changed = false;
      const tickets = prev.tickets.map((row) => {
        const definition = prev.selectedEventData?.tickets.find((item) => item.id === row.ticketID);
        if (
          prev.selectedEventData?.ticketContractVersion !== 2 ||
          (definition?.type !== 'GROUP' && definition?.ticketType !== 'GROUP')
        ) {
          return row;
        }
        const hydrated = {
          ...row,
          groupSizeMode: row.groupSizeMode ?? definition.groupSizeMode,
          pricingMode: row.pricingMode ?? definition.pricingMode,
          minimumSeats: row.minimumSeats ?? definition.minimumSeats,
          maximumSeats: row.maximumSeats === undefined ? definition.maximumSeats : row.maximumSeats,
        };
        if (
          hydrated.groupSizeMode === row.groupSizeMode &&
          hydrated.pricingMode === row.pricingMode &&
          hydrated.minimumSeats === row.minimumSeats &&
          hydrated.maximumSeats === row.maximumSeats
        ) {
          return row;
        }
        changed = true;
        return hydrated;
      });
      return changed ? { ...prev, tickets } : prev;
    });
  }, [isEditMode, formData.selectedEventData, setFormData]);

  // Calculate if all selected tickets have 0 price (free tickets)
  const isFreeTickets = useMemo(() => {
    const hasTicketsWithAttendees = formData.tickets?.some(
      (ticket) => ticket.ticketType && ticket.attendees?.length > 0,
    );

    if (!hasTicketsWithAttendees) return false;

    const ticketsTotal =
      formData.tickets?.reduce((total, ticket) => {
        return total + calculateDashboardTicketRowSubtotal(ticket, formData.selectedEventData);
      }, 0) || 0;

    const amountAfterDiscount = Math.max(0, ticketsTotal - (formData.totalPromoDiscount || 0));
    return amountAfterDiscount === 0;
  }, [formData.tickets, formData.totalPromoDiscount, formData.selectedEventData]);

  // Update payment type to 'Free' when tickets are free
  useEffect(() => {
    if (isFreeTickets) {
      handleFormDataChange({
        paymentType: 'Free',
        feeCovered: false,
      });
    } else if (formData.paymentType === 'Free') {
      const newPaymentType = isEditMode ? 'Cash' : 'Online';
      handleFormDataChange({
        paymentType: newPaymentType,
        feeCovered: isEditMode
          ? transaction?.coverFees === 'Yes' || transaction?.coverFees === true
          : true,
      });
    }
  }, [
    isFreeTickets,
    isEditMode,
    formData.paymentType,
    handleFormDataChange,
    transaction?.coverFees,
  ]);

  // Helper functions
  const shouldDisablePaymentType = () => {
    if (!isEditMode) return false;
    return paymentType === 'Online';
  };

  const shouldDisableOnlineOption = () => {
    if (!isEditMode) return false;
    return paymentType !== 'Online';
  };

  const shouldDisableEventFields = () => {
    return isEditMode && paymentType === 'Online';
  };

  const paymentTypeOptions = getEventOfflinePaymentMethods(
    formData.selectedEventData?.ticketContractVersion === 2 ||
      transaction?.ticketContractVersion === 2,
  );

  const hasProtectedInventoryHistory = (ticket?: Ticket): boolean =>
    ticket?.ticketInventoryReleased === true ||
    (ticket?.attendees || []).some(
      (attendee) =>
        (attendee.ticketInventoryReleaseReasons?.length || 0) > 0 ||
        Boolean(attendee.pendingRefundRequestID),
    );

  // Filter events from possibleForms
  const possibleEvents = useMemo(() => {
    return possibleForms.filter((form) => form.formType === 'EVENT');
  }, [possibleForms]);

  // Helper function to check if a specific ticket has promo codes configured
  const getTicketPromos = (ticketID: string) => {
    const selectedEvent = formData.selectedEventData;
    if (!selectedEvent?.tickets || !ticketID) return [];

    const eventTicket = selectedEvent.tickets.find((t) => t.id === ticketID);
    if (!eventTicket?.promos || !Array.isArray(eventTicket.promos)) return [];

    return eventTicket.promos;
  };

  // Helper function to get available inventory for a ticket type
  const getTicketAvailability = (
    ticketID: string,
  ): { available: number; totalQuantity: number; sold: number } => {
    const selectedEvent = formData.selectedEventData;
    if (!selectedEvent?.tickets || !ticketID) return { available: 0, totalQuantity: 0, sold: 0 };

    const eventTicket = selectedEvent.tickets.find((t) => t.id === ticketID);
    if (!eventTicket) return { available: 0, totalQuantity: 0, sold: 0 };

    const totalQuantity = eventTicket.quantity ?? 999;
    const credit = inventoryCredits[ticketID];
    const sold = Math.max(
      0,
      Number(eventTicket.sold ?? 0) -
        (eventTicket.type === 'GROUP' || eventTicket.ticketType === 'GROUP'
          ? Number(credit?.groups || 0)
          : Number(credit?.individuals || 0)),
    );
    const available = Math.max(0, totalQuantity - sold);

    return { available, totalQuantity, sold };
  };

  // Calculate how many units of a ticket type are currently selected
  const getSelectedTicketCount = (ticketID: string): number => {
    if (!ticketID) return 0;
    return (
      formData.tickets?.reduce((count, ticket) => {
        if (ticket.ticketID === ticketID) {
          if (ticket.ticketInventoryReleased) return count;
          const definition = formData.selectedEventData?.tickets.find(
            (candidate) => candidate.id === ticketID,
          );
          const isGroup = definition?.type === 'GROUP' || definition?.ticketType === 'GROUP';
          if (isGroup) return count + 1;
          return (
            count +
            ticket.attendees.filter(
              (attendee) => !(attendee.ticketInventoryReleaseReasons?.length || 0),
            ).length
          );
        }
        return count;
      }, 0) || 0
    );
  };

  const getRemainingSeatCapacity = (ticketID: string): number => {
    const definition = formData.selectedEventData?.tickets.find((ticket) => ticket.id === ticketID);
    const isGroup = definition?.type === 'GROUP' || definition?.ticketType === 'GROUP';
    if (!isGroup || !definition?.groupSizeMode) return Number.POSITIVE_INFINITY;
    const selectedSeats = formData.tickets.reduce(
      (sum, ticket) =>
        ticket.ticketID === ticketID
          ? sum +
            (ticket.attendees || []).filter(
              (attendee) => !(attendee.ticketInventoryReleaseReasons?.length || 0),
            ).length
          : sum,
      0,
    );
    const creditedSeats = Number(inventoryCredits[ticketID]?.seats || 0);
    return Math.max(
      Number(definition.seatCapacity || 0) -
        Number(definition.seatsSold || 0) +
        creditedSeats -
        selectedSeats,
      0,
    );
  };

  // Check if any ticket type still has available inventory
  const hasAnyTicketsAvailable = (): boolean => {
    const tickets = formData.selectedEventData?.tickets;
    if (!tickets) return false;

    return tickets.some((t) => {
      const { available } = getTicketAvailability(t.id);
      const selectedCount = getSelectedTicketCount(t.id);
      const hasGroupUnit = available - selectedCount > 0;
      const requiredSeats =
        t.groupSizeMode === 'FIXED'
          ? Number(t.groupSize ?? t.seats ?? 0)
          : t.groupSizeMode === 'MINIMUM'
            ? Number(t.minimumSeats || 0)
            : 0;
      return hasGroupUnit && getRemainingSeatCapacity(t.id) >= requiredSeats;
    });
  };

  // Handle event selection
  const handleEventSelection = (eventName: string) => {
    if (formData.tickets.some(hasProtectedInventoryHistory)) return;
    const selectedEvent = possibleForms.find((f) => f.formName === eventName);
    const selectedEventPaymentTypes = getEventOfflinePaymentMethods(
      selectedEvent?.ticketContractVersion === 2,
    ).map(formatOfflinePaymentMethod);

    setSelectedTickets([
      {
        ticketIndex: 0,
        ticketType: '',
        ticketID: '',
        ticketPrice: 0,
        seats: 1,
        customInputs: selectedEvent?.customInputs || [],
      },
    ]);

    setFormData((prev) => ({
      ...prev,
      selectedEvent: eventName,
      selectedEventData: selectedEvent || null,
      paymentType:
        selectedEvent?.ticketContractVersion === 2 &&
        prev.paymentType !== 'Online' &&
        !selectedEventPaymentTypes.includes(prev.paymentType || '')
          ? 'Online'
          : prev.paymentType,
      tickets: [
        {
          event: eventName,
          ticketType: '',
          ticketID: '',
          ticketPrice: 0,
          seats: 1,
          quantity: 1,
          attendees: [],
        },
      ],
      attendees: [],
    }));
  };

  // Handle ticket selection
  const handleTicketSelection = (ticketIndex: number, ticketName: string) => {
    if (hasProtectedInventoryHistory(formData.tickets[ticketIndex])) return;
    const selectedEvent = formData.selectedEventData;
    const ticket = selectedEvent?.tickets?.find((t) => t.name === ticketName);
    const ticketPrice =
      typeof ticket?.price === 'string' ? parseFloat(ticket.price) : ticket?.price || 0;
    const isGroupTicket = ticket?.type === 'GROUP' || ticket?.ticketType === 'GROUP';
    const rawSeats = !isGroupTicket
      ? 1
      : ticket?.groupSizeMode === 'MINIMUM'
        ? ticket.minimumSeats
        : ticket?.groupSize || ticket?.seats || 1;
    const seats = typeof rawSeats === 'string' ? parseInt(rawSeats, 10) : rawSeats;

    const attendeeCustomInputs =
      selectedEvent?.customInputs?.filter(
        (input) =>
          input.questionType === 'ATTENDEE' ||
          (input.inputType === 'attendee' && !input.questionType),
      ) || [];

    const purchaserCustomInputs =
      selectedEvent?.customInputs?.filter(
        (input) =>
          input.questionType === 'PURCHASER' ||
          (input.inputType === 'purchaser' && !input.questionType),
      ) || [];

    const updatedSelectedTickets: SelectedTicket[] = selectedTickets.map((t, i) =>
      i === ticketIndex
        ? {
            ...t,
            ticketType: ticketName,
            ticketID: ticket?.id || '',
            ticketPrice: ticketPrice,
            seats: seats,
            customInputs: selectedEvent?.customInputs || [],
          }
        : t,
    );

    setSelectedTickets(updatedSelectedTickets);

    const purchasedGroupID =
      formData.selectedEventData?.ticketContractVersion === 2 &&
      (ticket?.type === 'GROUP' || ticket?.ticketType === 'GROUP')
        ? generatePurchasedGroupID()
        : undefined;
    const ticketAttendees: TicketAttendee[] = Array(seats)
      .fill(null)
      .map((_, index) => ({
        firstName: '',
        lastName: '',
        email: '',
        confirmationID: generateConfirmationID(),
        ...(purchasedGroupID ? { purchasedGroupID, seatIndex: index + 1 } : {}),
        customInputs: attendeeCustomInputs.map((input) => ({
          ...input,
          value: '',
        })),
      }));

    const formDataPurchaserCustomInputs = purchaserCustomInputs.map((input) => ({
      ...input,
      value: '',
    }));

    setFormData((prev) => ({
      ...prev,
      tickets: prev.tickets.map((t, i) =>
        i === ticketIndex
          ? {
              ...t,
              ticketType: ticketName,
              ticketID: ticket?.id || '',
              ticketPrice: ticketPrice,
              seats: seats,
              groupSizeMode: ticket?.groupSizeMode,
              pricingMode: ticket?.pricingMode,
              minimumSeats: ticket?.minimumSeats,
              maximumSeats: ticket?.maximumSeats,
              purchasedGroupID,
              attendees: ticketAttendees,
            }
          : t,
      ),
      purchaserCustomInputs:
        prev.purchaserCustomInputs && prev.purchaserCustomInputs.length > 0
          ? prev.purchaserCustomInputs
          : formDataPurchaserCustomInputs,
    }));
  };

  const handleMinimumGroupSizeChange = (ticketIndex: number, nextSeatCount: number) => {
    setFormData((prev) => {
      const row = prev.tickets[ticketIndex];
      const definition = prev.selectedEventData?.tickets.find((item) => item.id === row.ticketID);
      if (!row || definition?.groupSizeMode !== 'MINIMUM') return prev;
      if (hasProtectedInventoryHistory(row)) {
        return prev;
      }
      const minimum = Number(definition.minimumSeats || 0);
      const remainingSeats = Math.max(
        Number(definition.seatCapacity || 0) -
          Number(definition.seatsSold || 0) +
          Number(inventoryCredits[row.ticketID || '']?.seats || 0),
        0,
      );
      const selectedElsewhere = prev.tickets.reduce(
        (sum, candidate, index) =>
          index === ticketIndex || candidate.ticketID !== row.ticketID
            ? sum
            : sum +
              candidate.attendees.filter(
                (attendee) => !(attendee.ticketInventoryReleaseReasons?.length || 0),
              ).length,
        0,
      );
      const maximum = Math.min(
        definition.maximumSeats == null ? remainingSeats : Number(definition.maximumSeats),
        remainingSeats - selectedElsewhere,
      );
      if (!Number.isInteger(nextSeatCount) || nextSeatCount < minimum || nextSeatCount > maximum) {
        return prev;
      }
      const attendeeTemplate = row.attendees[0]?.customInputs || [];
      const attendees = Array.from({ length: nextSeatCount }, (_, index) =>
        row.attendees[index]
          ? {
              ...row.attendees[index],
              purchasedGroupID: row.purchasedGroupID,
              seatIndex: index + 1,
            }
          : {
              firstName: '',
              lastName: '',
              email: '',
              confirmationID: generateConfirmationID(),
              customInputs: attendeeTemplate.map((input) => ({ ...input, value: '' })),
              purchasedGroupID: row.purchasedGroupID,
              seatIndex: index + 1,
            },
      );
      return {
        ...prev,
        tickets: prev.tickets.map((candidate, index) =>
          index === ticketIndex ? { ...candidate, seats: nextSeatCount, attendees } : candidate,
        ),
      };
    });
  };

  // Handle adding a new ticket
  const handleAddTicket = () => {
    const newSelectedTicket = {
      ticketIndex: selectedTickets.length,
      ticketType: '',
      ticketID: '',
      ticketPrice: 0,
      seats: 1,
      customInputs: formData.selectedEventData?.customInputs || [],
    };
    setSelectedTickets((prev) => [...prev, newSelectedTicket]);

    const newTicket: Ticket = {
      event: formData.selectedEvent,
      ticketType: '',
      ticketID: '',
      ticketPrice: 0,
      seats: 1,
      quantity: 1,
      attendees: [],
    };
    setFormData((prev) => ({
      ...prev,
      tickets: [...prev.tickets, newTicket],
    }));
  };

  // Handle removing a ticket
  const handleRemoveTicket = (ticketIndex: number) => {
    if (hasProtectedInventoryHistory(formData.tickets[ticketIndex])) return;
    const updatedSelectedTickets = selectedTickets.filter((_, i) => i !== ticketIndex);
    const renumberedSelectedTickets = updatedSelectedTickets.map((t, i) => ({
      ...t,
      ticketIndex: i,
    }));
    setSelectedTickets(renumberedSelectedTickets);

    setFormData((prev) => ({
      ...prev,
      tickets: prev.tickets.filter((_, i) => i !== ticketIndex),
    }));
  };

  // Handle attendee field changes
  const handleAttendeeChange = (
    ticketIndex: number,
    attendeeIndex: number,
    field: string,
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      tickets: prev.tickets.map((ticket, tIdx) =>
        tIdx === ticketIndex
          ? {
              ...ticket,
              attendees: ticket.attendees.map((attendee, aIdx) =>
                aIdx === attendeeIndex
                  ? {
                      ...attendee,
                      [field]: value,
                    }
                  : attendee,
              ),
            }
          : ticket,
      ),
    }));
  };

  // Handle attendee custom input changes
  const handleAttendeeCustomInputChange = (
    ticketIndex: number,
    attendeeIndex: number,
    inputIndex: number,
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      tickets: prev.tickets.map((ticket, tIdx) =>
        tIdx === ticketIndex
          ? {
              ...ticket,
              attendees: ticket.attendees.map((attendee, aIdx) =>
                aIdx === attendeeIndex
                  ? {
                      ...attendee,
                      customInputs: attendee.customInputs.map((input, iIdx) =>
                        iIdx === inputIndex
                          ? {
                              ...input,
                              value: value,
                            }
                          : input,
                      ),
                    }
                  : attendee,
              ),
            }
          : ticket,
      ),
    }));
  };

  return (
    <div>
      {/* Payment Type - Hidden when free tickets are selected */}
      {!isFreeTickets && (
        <div className="mb-2">
          <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
            Payment Type
          </Label>
          <select
            className={`block h-7 w-full rounded-md border-edge px-2 py-0 text-sm leading-tight focus:border-sage focus:outline-none focus:ring-sage ${
              shouldDisablePaymentType()
                ? 'cursor-not-allowed bg-paper-inset text-ink-muted'
                : 'bg-white'
            }`}
            value={paymentType}
            onChange={(e) => {
              handleFormDataChange({ paymentType: e.target.value });
              if (e.target.value !== 'Online') {
                handleFormDataChange({ feeCovered: false });
              } else {
                handleFormDataChange({ feeCovered: true });
              }
            }}
            onFocus={() => handleFieldFocus('paymentType')}
            disabled={shouldDisablePaymentType()}
          >
            <option value="Online" disabled={shouldDisableOnlineOption()}>
              Online
            </option>
            {paymentTypeOptions.map((paymentMethod) => {
              const label = formatOfflinePaymentMethod(paymentMethod);
              return (
                <option key={paymentMethod} value={label}>
                  {label}
                </option>
              );
            })}
          </select>
        </div>
      )}

      {/* Event Flow UI */}
      <div>
        {/* Event Selection */}
        <div className="mb-2">
          <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
            Event
          </Label>
          <select
            className={`block h-7 w-full rounded-md px-2 py-0 text-sm leading-tight focus:border-sage focus:outline-none focus:ring-sage ${
              invalidFields.has('selectedEvent')
                ? 'border-red-500'
                : shouldDisableEventFields()
                  ? 'cursor-not-allowed border-edge bg-paper-inset text-ink-muted'
                  : 'border-edge bg-white'
            }`}
            value={formData.selectedEvent || ''}
            onChange={(e) => handleEventSelection(e.target.value)}
            onFocus={() => handleFieldFocus('selectedEvent')}
            disabled={
              shouldDisableEventFields() || formData.tickets.some(hasProtectedInventoryHistory)
            }
          >
            <option value="" disabled>
              Select Event
            </option>
            {[...possibleEvents]
              .sort((a, b) => a.formName.localeCompare(b.formName))
              .map((event) => (
                <option key={event.elementID} value={event.formName}>
                  {event.formName}
                </option>
              ))}
          </select>
        </div>

        {/* Ticket Selection Section with Nested Attendee Information */}
        {formData.selectedEvent && (
          <div className="mb-2">
            <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
              Tickets
            </Label>
            <div className="flex flex-col gap-2">
              {formData?.tickets?.map((ticket, ticketIndex) => {
                return (
                  <div key={ticketIndex}>
                    <div className="relative space-y-2 rounded-lg border border-edge bg-paper-muted px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <select
                            className={`block h-7 w-full rounded-md px-2 py-0 text-sm leading-tight focus:border-sage focus:outline-none focus:ring-sage ${
                              invalidFields.has(`ticket-${ticketIndex}`)
                                ? 'border-red-500'
                                : shouldDisableEventFields()
                                  ? 'cursor-not-allowed border-edge bg-paper-inset text-ink-muted'
                                  : 'border-edge bg-white'
                            }`}
                            value={ticket.ticketType || ''}
                            onChange={(e) => handleTicketSelection(ticketIndex, e.target.value)}
                            onFocus={() => handleFieldFocus(`ticket-${ticketIndex}`)}
                            disabled={
                              shouldDisableEventFields() || hasProtectedInventoryHistory(ticket)
                            }
                          >
                            <option value="" disabled>
                              Select Ticket Type
                            </option>
                            {formData.selectedEventData?.tickets?.map((t) => {
                              const { available } = getTicketAvailability(t.id);
                              const selectedCount = getSelectedTicketCount(t.id);
                              const remainingAvailable = available - selectedCount;
                              const requiredSeats =
                                t.groupSizeMode === 'FIXED'
                                  ? Number(t.groupSize ?? t.seats ?? 0)
                                  : t.groupSizeMode === 'MINIMUM'
                                    ? Number(t.minimumSeats || 0)
                                    : 0;
                              const lacksRequiredSeats =
                                getRemainingSeatCapacity(t.id) < requiredSeats;
                              const isSoldOut =
                                (remainingAvailable <= 0 || lacksRequiredSeats) &&
                                ticket.ticketID !== t.id;
                              return (
                                <option key={t.id} value={t.name} disabled={isSoldOut}>
                                  {t.name} - ${parseFloat(String(t.price || 0)).toFixed(2)}
                                  {t.pricingMode === 'PER_SEAT' ? ' per seat' : ''}
                                  {Number(t.seats) > 1 && ` (${t.seats} seats)`}
                                  {t.groupSizeMode === 'MINIMUM' &&
                                    ` (${t.minimumSeats}${
                                      t.maximumSeats == null ? '+' : `–${t.maximumSeats}`
                                    } seats)`}
                                  {isSoldOut && ' - SOLD OUT'}
                                  {!isSoldOut &&
                                    remainingAvailable <= 5 &&
                                    remainingAvailable > 0 &&
                                    ` (${remainingAvailable} left)`}
                                </option>
                              );
                            })}
                          </select>
                        </div>

                        {ticketIndex > 0 && !hasProtectedInventoryHistory(ticket) && (
                          <button
                            type="button"
                            onClick={() => handleRemoveTicket(ticketIndex)}
                            className="text-ink-muted hover:text-ink-secondary"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M18 6L6 18" />
                              <path d="M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>

                      {ticket.groupSizeMode === 'MINIMUM' && ticket.ticketType && (
                        <div className="flex items-center justify-between rounded-md border border-edge bg-white px-2 py-1.5 text-sm">
                          <span>Seats in this group</span>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="h-6 w-6 rounded-full border border-edge disabled:opacity-40"
                              onClick={() =>
                                handleMinimumGroupSizeChange(
                                  ticketIndex,
                                  ticket.attendees.length - 1,
                                )
                              }
                              disabled={
                                hasProtectedInventoryHistory(ticket) ||
                                ticket.attendees.length <= Number(ticket.minimumSeats || 0)
                              }
                            >
                              −
                            </button>
                            <span className="min-w-5 text-center font-medium">
                              {ticket.attendees.length}
                            </span>
                            <button
                              type="button"
                              className="h-6 w-6 rounded-full border border-edge disabled:opacity-40"
                              onClick={() =>
                                handleMinimumGroupSizeChange(
                                  ticketIndex,
                                  ticket.attendees.length + 1,
                                )
                              }
                              disabled={
                                hasProtectedInventoryHistory(ticket) ||
                                (ticket.maximumSeats != null &&
                                  ticket.attendees.length >= Number(ticket.maximumSeats)) ||
                                getRemainingSeatCapacity(ticket.ticketID || '') <= 0
                              }
                            >
                              +
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Nested Attendee Information */}
                      {ticket.ticketType && ticket.attendees?.length > 0 && (
                        <div className="space-y-2">
                          {ticket.attendees.map((attendee, attendeeIndex) => {
                            const fieldPrefix = `ticket-${ticketIndex}-attendee-${attendeeIndex}`;

                            return (
                              <div
                                key={attendeeIndex}
                                className={`relative space-y-2 rounded-lg border px-3 py-2 ${
                                  invalidFields.has(`${fieldPrefix}-duplicate`)
                                    ? 'border-red-500'
                                    : 'border-edge bg-paper-inset'
                                }`}
                              >
                                {ticket.attendees.length > 1 && (
                                  <div className="pb-0">
                                    <label className="block font-inter text-xs font-medium leading-5 text-ink-muted">
                                      Seat {attendeeIndex + 1}
                                    </label>
                                  </div>
                                )}

                                <div>
                                  <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                                    First Name
                                  </Label>
                                  <Input
                                    className={`block h-7 w-full rounded-md px-2 py-0 text-sm leading-tight ${
                                      invalidFields.has(`${fieldPrefix}-firstName`)
                                        ? 'border-red-500'
                                        : 'border-edge bg-white'
                                    }`}
                                    placeholder="First Name"
                                    value={attendee.firstName || ''}
                                    onChange={(e) =>
                                      handleAttendeeChange(
                                        ticketIndex,
                                        attendeeIndex,
                                        'firstName',
                                        e.target.value,
                                      )
                                    }
                                    onFocus={() => handleFieldFocus(`${fieldPrefix}-firstName`)}
                                  />
                                </div>

                                <div>
                                  <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                                    Last Name
                                  </Label>
                                  <Input
                                    className={`block h-7 w-full rounded-md px-2 py-0 text-sm leading-tight ${
                                      invalidFields.has(`${fieldPrefix}-lastName`)
                                        ? 'border-red-500'
                                        : 'border-edge bg-white'
                                    }`}
                                    placeholder="Last Name"
                                    value={attendee.lastName || ''}
                                    onChange={(e) =>
                                      handleAttendeeChange(
                                        ticketIndex,
                                        attendeeIndex,
                                        'lastName',
                                        e.target.value,
                                      )
                                    }
                                    onFocus={() => handleFieldFocus(`${fieldPrefix}-lastName`)}
                                  />
                                </div>

                                <div>
                                  <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                                    Email
                                  </Label>
                                  <Input
                                    className={`block h-7 w-full rounded-md px-2 py-0 text-sm leading-tight ${
                                      invalidFields.has(`${fieldPrefix}-email`)
                                        ? 'border-red-500'
                                        : 'border-edge bg-white'
                                    }`}
                                    type="email"
                                    placeholder="Email"
                                    value={attendee.email || ''}
                                    onChange={(e) =>
                                      handleAttendeeChange(
                                        ticketIndex,
                                        attendeeIndex,
                                        'email',
                                        e.target.value,
                                      )
                                    }
                                    onFocus={() => handleFieldFocus(`${fieldPrefix}-email`)}
                                  />
                                </div>

                                {/* Attendee Custom Inputs */}
                                {attendee.customInputs?.map((input, inputIndex) => {
                                  const inputType = input.type?.toUpperCase() || '';
                                  const isTextInput =
                                    inputType === 'TEXT' ||
                                    inputType === 'SHORT_ANSWER' ||
                                    inputType === 'SHORT_TEXT_INPUT';
                                  const isTextarea =
                                    inputType === 'TEXTAREA' ||
                                    inputType === 'PARAGRAPH' ||
                                    inputType === 'LONG_TEXT_INPUT';
                                  const isDropdown =
                                    inputType === 'DROPDOWN' ||
                                    inputType === 'MULTIPLE_CHOICE' ||
                                    inputType === 'SINGLE_SELECTION_DROPDOWN';
                                  const isMultiSelect = inputType === 'MULTI_SELECTION_DROPDOWN';

                                  return (
                                    <div key={inputIndex}>
                                      <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                                        {input.title}
                                        {input.required && <span className="text-red-500"> *</span>}
                                      </Label>
                                      {isTextInput ? (
                                        <Input
                                          className={`block h-7 w-full rounded-md px-2 py-0 text-sm leading-tight ${
                                            invalidFields.has(`${fieldPrefix}-custom-${inputIndex}`)
                                              ? 'border-red-500'
                                              : 'border-edge bg-white'
                                          }`}
                                          placeholder={input.placeholder || ''}
                                          value={input.value || ''}
                                          onChange={(e) =>
                                            handleAttendeeCustomInputChange(
                                              ticketIndex,
                                              attendeeIndex,
                                              inputIndex,
                                              e.target.value,
                                            )
                                          }
                                          onFocus={() =>
                                            handleFieldFocus(`${fieldPrefix}-custom-${inputIndex}`)
                                          }
                                        />
                                      ) : isTextarea ? (
                                        <textarea
                                          className={`block w-full rounded-md px-2 py-1 text-sm ${
                                            invalidFields.has(`${fieldPrefix}-custom-${inputIndex}`)
                                              ? 'border-red-500'
                                              : 'border-edge bg-white'
                                          }`}
                                          rows={3}
                                          placeholder={input.placeholder || ''}
                                          value={input.value || ''}
                                          onChange={(e) =>
                                            handleAttendeeCustomInputChange(
                                              ticketIndex,
                                              attendeeIndex,
                                              inputIndex,
                                              e.target.value,
                                            )
                                          }
                                          onFocus={() =>
                                            handleFieldFocus(`${fieldPrefix}-custom-${inputIndex}`)
                                          }
                                        />
                                      ) : isDropdown ? (
                                        <select
                                          className={`block h-7 w-full rounded-md px-2 py-0 text-sm ${
                                            invalidFields.has(`${fieldPrefix}-custom-${inputIndex}`)
                                              ? 'border-red-500'
                                              : 'border-edge bg-white'
                                          }`}
                                          value={input.value || ''}
                                          onChange={(e) =>
                                            handleAttendeeCustomInputChange(
                                              ticketIndex,
                                              attendeeIndex,
                                              inputIndex,
                                              e.target.value,
                                            )
                                          }
                                          onFocus={() =>
                                            handleFieldFocus(`${fieldPrefix}-custom-${inputIndex}`)
                                          }
                                        >
                                          <option value="" disabled>
                                            Select an option
                                          </option>
                                          {input.options?.map((option, optIndex) => (
                                            <option key={optIndex} value={option}>
                                              {option}
                                            </option>
                                          ))}
                                        </select>
                                      ) : isMultiSelect ? (
                                        <MultiSelectDropdownNoCreate
                                          height={28}
                                          options={
                                            input.options?.map((option) => ({
                                              value: option,
                                              label: option,
                                            })) || []
                                          }
                                          items={(input.value || '')
                                            .split(',')
                                            .filter((v: string) => v.trim() !== '')}
                                          setItems={(selectedValues: string[]) => {
                                            handleAttendeeCustomInputChange(
                                              ticketIndex,
                                              attendeeIndex,
                                              inputIndex,
                                              selectedValues.join(','),
                                            );
                                          }}
                                          error={invalidFields.has(
                                            `${fieldPrefix}-custom-${inputIndex}`,
                                          )}
                                        />
                                      ) : null}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Add Ticket Button */}
              {formData.tickets?.some((t) => t.ticketType) &&
                hasAnyTicketsAvailable() &&
                !shouldDisableEventFields() && (
                  <button
                    type="button"
                    onClick={handleAddTicket}
                    className="flex w-full items-center text-sm font-medium text-ink-muted hover:text-ink-secondary"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mr-1"
                    >
                      <path d="M12 5v14" />
                      <path d="M5 12h14" />
                    </svg>
                    Add Ticket
                  </button>
                )}
            </div>
          </div>
        )}

        {/* Promo Code Section */}
        {formData.selectedEvent &&
          (formData.appliedPromoCodes?.length > 0 ||
            formData.tickets?.some(
              (ticket) => ticket.ticketID && getTicketPromos(ticket.ticketID).length > 0,
            )) && (
            <div className="mb-4">
              <Label
                className={`block font-inter text-sm font-medium leading-5 ${
                  shouldDisableEventFields() ? 'text-ink-muted' : 'text-ink-muted'
                }`}
              >
                Promo Code (Optional)
              </Label>

              {formData.appliedPromoCodes?.length > 0 && (
                <div className="mb-2 space-y-2">
                  {formData.appliedPromoCodes.map((promo, index) => (
                    <div
                      key={index}
                      className={`flex items-center gap-2 rounded-md border px-3 py-2 ${
                        shouldDisableEventFields()
                          ? 'border-edge bg-paper-inset'
                          : 'border-green-500 bg-[#EDF3ED]'
                      }`}
                    >
                      <div className="flex-1">
                        <span
                          className={`text-sm font-medium ${
                            shouldDisableEventFields() ? 'text-ink-muted' : 'text-[#4A6B4A]'
                          }`}
                        >
                          {promo.code}
                        </span>
                        <span
                          className={`ml-2 text-xs ${
                            shouldDisableEventFields() ? 'text-ink-muted' : 'text-green-600'
                          }`}
                        >
                          -${promo.discount?.toFixed(2)} discount applied
                        </span>
                      </div>
                      {!shouldDisableEventFields() && (
                        <button
                          type="button"
                          onClick={() => removePromoCode(promo.code)}
                          className="text-green-600 hover:text-green-800"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M18 6L6 18" />
                            <path d="M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {!formData.appliedPromoCodes?.length && (
                <>
                  <div className="relative flex items-center gap-2">
                    <div
                      className={`relative flex flex-1 items-center rounded-md border ${
                        shouldDisableEventFields() ? 'bg-paper-inset' : 'bg-paper-muted'
                      } ${
                        promoMessage === 'Invalid code' ||
                        promoMessage === 'Code expired' ||
                        promoMessage === 'Error' ||
                        promoMessage === 'Already applied'
                          ? 'border-red-500'
                          : 'border-edge'
                      }`}
                    >
                      <input
                        type="text"
                        placeholder="Enter promo code"
                        value={formData.promoCode || ''}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            promoCode: e.target.value.toUpperCase(),
                          }))
                        }
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            validatePromoCode(formData.promoCode);
                          }
                        }}
                        disabled={isPromoProcessing || shouldDisableEventFields()}
                        className={`block h-7 w-full rounded-md border-0 bg-transparent px-2 py-0 pr-16 text-sm leading-tight focus:outline-none focus:ring-0 ${
                          shouldDisableEventFields() ? 'cursor-not-allowed text-ink-muted' : ''
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => validatePromoCode(formData.promoCode)}
                        disabled={
                          !formData.promoCode?.trim() ||
                          isPromoProcessing ||
                          shouldDisableEventFields()
                        }
                        className="absolute right-1 whitespace-nowrap px-2 py-1 text-xs font-medium text-ink-secondary hover:text-ink disabled:text-ink-muted disabled:hover:text-ink-muted"
                      >
                        {promoMessage}
                      </button>
                    </div>
                  </div>

                  {!shouldDisableEventFields() &&
                    (promoMessage === 'Invalid code' ||
                      promoMessage === 'Code expired' ||
                      promoMessage === 'Already applied') && (
                      <p className="mt-1 text-xs text-red-500">
                        {promoMessage}. Please try another code.
                      </p>
                    )}
                </>
              )}
            </div>
          )}
      </div>
    </div>
  );
}
